import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
  FaBed, FaUsers, FaClock, FaUtensils, FaWater, 
  FaShip, FaHiking, FaFish, FaWifi, FaParking,
  FaVideo, FaBolt, FaTree, FaHome, FaUmbrellaBeach,
  FaChild, FaBirthdayCake, FaFire, FaHandshake, FaMountain,
  FaPlus, FaEdit, FaTrash
} from 'react-icons/fa';

// Premium color palette
const COLORS = {
  primary: '#1e293b',
  secondary: '#334155',
  accent: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  dark: '#0f172a',
  light: '#f8fafc',
  white: '#ffffff',
  gray: '#64748b',
  lightGray: '#f1f5f9',
  gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
};

// Premium styled components
const Form = styled.form`
  background: ${COLORS.white};
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid ${COLORS.lightGray};
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${COLORS.primary};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 0.5rem;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${COLORS.lightGray};
  border-radius: 0.5rem;
  font-size: 1rem;
  background: ${COLORS.white};
  
  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Button = styled.button`
display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #1e3a8a;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 2rem;
  
  &:hover {
    background-color: #1c3175;
  
  &:disabled {
    background: ${COLORS.lightGray};
    color: ${COLORS.gray};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const FacilitiesList = styled.div`
  margin-top: 2rem;
`;

const FacilityItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.lightGray};
  border-radius: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const IconWrapper = styled.div`
  font-size: 1.5rem;
  color: ${COLORS.accent};
  margin-right: 1rem;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FacilityDetails = styled.div`
  flex: 1;
`;

const FacilityText = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #4b5563;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const EditButton = styled(Button)`
  padding: 0.5rem 1rem;
  background-color: #f59e0b;
  
  &:hover {
    background-color: #d97706;
  }
`;

const RemoveButton = styled(Button)`
  padding: 0.5rem 1rem;
  background-color: #ef4444;
  
  &:hover {
    background-color: #dc2626;
  }
`;

const Message = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  background-color: ${props => props.type === 'error' ? '#fee2e2' : '#d1fae5'};
  color: ${props => props.type === 'error' ? '#b91c1c' : '#065f46'};
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const CancelButton = styled(Button)`
  background-color: #9ca3af;
  
  &:hover {
    background-color: #6b7280;
  }
`;

const SaveButton = styled(Button)``;

// Available icons mapping
const iconComponents = {
  FaBed: <FaBed />,
  FaUsers: <FaUsers />,
  FaClock: <FaClock />,
  FaUtensils: <FaUtensils />,
  FaWater: <FaWater />,
  FaShip: <FaShip />,
  FaHiking: <FaHiking />,
  FaFish: <FaFish />,
  FaWifi: <FaWifi />,
  FaParking: <FaParking />,
  FaVideo: <FaVideo />,
  FaBolt: <FaBolt />,
  FaTree: <FaTree />,
  FaHome: <FaHome />,
  FaUmbrellaBeach: <FaUmbrellaBeach />,
  FaChild: <FaChild />,
  FaBirthdayCake: <FaBirthdayCake />,
  FaFire: <FaFire />,
  FaHandshake: <FaHandshake />,
  FaMountain: <FaMountain />
};

const FacilitiesManagement = () => {
  const [facilities, setFacilities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentFacility, setCurrentFacility] = useState(null);
  const [formData, setFormData] = useState({
    text: '',
    iconName: 'FaBed'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      const facilitiesCollection = collection(db, 'facilities');
      const snapshot = await getDocs(facilitiesCollection);
      
      const facilitiesData = [];
      snapshot.forEach((doc) => {
        facilitiesData.push({ id: doc.id, ...doc.data() });
      });
      
      setFacilities(facilitiesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setMessage({ text: 'Failed to load facilities', type: 'error' });
      setLoading(false);
    }
  };

  const handleAddFacility = () => {
    setCurrentFacility(null);
    setFormData({
      text: '',
      iconName: 'FaBed'
    });
    setShowModal(true);
  };

  const handleEditFacility = (facility) => {
    setCurrentFacility(facility);
    setFormData({
      text: facility.text || '',
      iconName: facility.iconName || 'FaBed'
    });
    setShowModal(true);
  };

  const handleDeleteFacility = async (id) => {
    if (window.confirm('Are you sure you want to delete this facility?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, 'facilities', id));
        setMessage({ text: 'Facility deleted successfully', type: 'success' });
        fetchFacilities();
      } catch (error) {
        console.error('Error deleting facility:', error);
        setMessage({ text: 'Failed to delete facility', type: 'error' });
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      if (currentFacility) {
        // Update existing facility
        await updateDoc(doc(db, 'facilities', currentFacility.id), {
          text: formData.text,
          iconName: formData.iconName,
          updatedAt: new Date()
        });
        
        setMessage({ text: 'Facility updated successfully', type: 'success' });
      } else {
        // Add new facility
        await addDoc(collection(db, 'facilities'), {
          text: formData.text,
          iconName: formData.iconName,
          createdAt: new Date()
        });
        
        setMessage({ text: 'Facility added successfully', type: 'success' });
      }
      
      // Refresh the facilities list
      fetchFacilities();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving facility:', error);
      setMessage({ text: 'Failed to save facility', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {message.text && (
        <Message type={message.type}>{message.text}</Message>
      )}
      
      <Button onClick={handleAddFacility}>
        <FaPlus style={{ marginRight: '0.5rem' }} /> Add New Facility
      </Button>
      
      <FacilitiesList>
        {facilities.map((facility) => (
          <FacilityItem key={facility.id}>
            <IconWrapper>
              {iconComponents[facility.iconName] || <FaBed />}
            </IconWrapper>
            <FacilityDetails>
              <FacilityText>{facility.text}</FacilityText>
            </FacilityDetails>
            <ActionButtons>
              <EditButton onClick={() => handleEditFacility(facility)}>
                <FaEdit />
              </EditButton>
              <RemoveButton onClick={() => handleDeleteFacility(facility.id)}>
                <FaTrash />
              </RemoveButton>
            </ActionButtons>
          </FacilityItem>
        ))}
        
        {facilities.length === 0 && !loading && (
          <p>No facilities added yet.</p>
        )}
        
        {loading && <p>Loading facilities...</p>}
      </FacilitiesList>
      
      {showModal && (
        <Modal>
          <ModalContent>
            <ModalTitle>
              {currentFacility ? 'Edit Facility' : 'Add New Facility'}
            </ModalTitle>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="text">Facility Text</Label>
                <Input 
                  type="text" 
                  id="text" 
                  name="text" 
                  value={formData.text} 
                  onChange={handleInputChange}
                  placeholder="Enter facility description"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="iconName">Icon</Label>
                <Select 
                  id="iconName" 
                  name="iconName" 
                  value={formData.iconName} 
                  onChange={handleInputChange}
                  required
                >
                  {Object.keys(iconComponents).map((iconName) => (
                    <option key={iconName} value={iconName}>
                      {iconName.replace('Fa', '')}
                    </option>
                  ))}
                </Select>
                <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '0.5rem' }}>Preview:</span>
                  <IconWrapper>
                    {iconComponents[formData.iconName] || <FaBed />}
                  </IconWrapper>
                </div>
              </FormGroup>
              
              <ModalActions>
                <CancelButton type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </CancelButton>
                <SaveButton type="submit" disabled={loading}>
                  {loading ? 'Saving...' : currentFacility ? 'Update Facility' : 'Add Facility'}
                </SaveButton>
              </ModalActions>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default FacilitiesManagement;
